// src/features/tasks/components/TaskCategoryForm.jsx
import React, { memo, useCallback, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  Button,
} from '@mui/material';
import {
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { FormStack } from '../../../components/common/form';

const PRESET_COLORS = [
  '#f44336', '#e91e63', '#9c27b0', '#673ab7',
  '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4',
  '#009688', '#4caf50', '#8bc34a', '#cddc39',
  '#ffeb3b', '#ffc107', '#ff9800', '#ff5722',
  '#795548', '#9e9e9e', '#607d8b', '#000000',
];

const TaskCategoryForm = memo(function TaskCategoryForm({
  formData,
  onInputChange,
  validationErrors = {},
  loading = false,
  mode = 'create', // 'create' or 'edit'
}) {
  const { t } = useTranslation(['common', 'tasks']);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  const handleNameChange = useCallback((event) => {
    onInputChange('name', event.target.value);
  }, [onInputChange]);

  const handleDescriptionChange = useCallback((event) => {
    onInputChange('description', event.target.value);
  }, [onInputChange]);

  const handleColorChange = useCallback((color) => {
    onInputChange('color_code', color);
    setColorPickerOpen(false);
  }, [onInputChange]);

  const toggleColorPicker = useCallback(() => {
    setColorPickerOpen(prev => !prev);
  }, []);

  return (
    // Rendered inside the create/edit dialogs, which supply the title and frame
    <Box>
        <FormStack>
          <TextField
            fullWidth
            label={t('tasks:category_form.category_name')}
            value={formData.name || ''}
            onChange={handleNameChange}
            error={!!validationErrors.name}
            helperText={validationErrors.name}
            required
            disabled={loading}
            placeholder={t('tasks:category_form.name_placeholder')}
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            fullWidth
            label={t('common:labels.description')}
            multiline
            rows={3}
            value={formData.description || ''}
            onChange={handleDescriptionChange}
            error={!!validationErrors.description}
            helperText={validationErrors.description || t('tasks:category_form.description_helper')}
            disabled={loading}
            placeholder={t('tasks:category_form.description_placeholder')}
            inputProps={{ maxLength: 1000 }}
          />
          <Typography variant="subtitle2" gutterBottom>
            {t('tasks:category_form.category_color')}
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="outlined"
              startIcon={<PaletteIcon />}
              onClick={toggleColorPicker}
              disabled={loading}
              sx={{
                minWidth: 120,
                backgroundColor: formData.color_code || 'transparent',
                color: formData.color_code ? '#fff' : 'inherit',
                '&:hover': {
                  backgroundColor: formData.color_code || 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              {formData.color_code 
                ? t('tasks:category_form.change_color') 
                : t('tasks:category_form.pick_color')
              }
            </Button>

            {formData.color_code && (
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: formData.color_code,
                    border: '2px solid',
                    borderColor: 'divider',
                  }}
                />
                <Typography variant="body2" fontFamily="monospace">
                  {formData.color_code}
                </Typography>
                <Button
                  size="small"
                  onClick={() => handleColorChange(null)}
                  disabled={loading}
                >
                  {t('tasks:category_form.clear_color')}
                </Button>
              </Box>
            )}
          </Box>

          {colorPickerOpen && (
            <Box mt={2} p={2} border={1} borderColor="divider" borderRadius={1}>
              <Typography variant="subtitle2" gutterBottom>
                {t('tasks:category_form.choose_color')}
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={2}>
                {PRESET_COLORS.map((color) => (
                  <Box
                    key={color}
                    onClick={() => handleColorChange(color)}
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: color,
                      borderRadius: 1,
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: formData.color_code === color ? 'primary.main' : 'transparent',
                      '&:hover': {
                        transform: 'scale(1.1)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  />
                ))}
              </Box>

              <Box mt={2}>
                <TextField
                  fullWidth
                  label={t('tasks:category_form.custom_color')}
                  value={formData.color_code || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                      onInputChange('color_code', value);
                    }
                  }}
                  placeholder="#FF5733"
                  disabled={loading}
                  inputProps={{ 
                    maxLength: 7,
                    pattern: '^#[0-9A-Fa-f]{6}$'
                  }}
                  helperText={t('tasks:category_form.custom_color_helper')}
                />
              </Box>
            </Box>
          )}

          {validationErrors.color_code && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {validationErrors.color_code}
            </Typography>
          )}
        </FormStack>

        {mode === 'create' && (
          <Alert severity="info" sx={{ mt: 5 }}>
            {t('tasks:category_form.after_creation_info')}
          </Alert>
        )}
    </Box>
  );
});

export default TaskCategoryForm;